import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { fetchMedia } from '../../../services/whatsappApi';
import { attachmentKind, downloadName, mediaFailure } from '../../../whatsapp/attachments';
import type { AttachmentKind } from '../../../whatsapp/attachments';
import type { MediaInfo, ThreadMessage } from '../../../types/whatsapp';
import { Modal } from '../Modal';

const ICON_PROPS = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

const ImageIcon = () => (
  <svg {...ICON_PROPS} width={28} height={28}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="9" cy="9" r="2" />
    <path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21" />
  </svg>
);

const VideoIcon = () => (
  <svg {...ICON_PROPS} width={28} height={28}>
    <rect x="2" y="6" width="14" height="12" rx="2" />
    <path d="m22 8-6 4 6 4V8Z" />
  </svg>
);

const PlayIcon = () => (
  <svg {...ICON_PROPS} width={20} height={20}>
    <path d="M7 4.5v15l12-7.5-12-7.5Z" />
  </svg>
);

const FileIcon = ({ size = 16 }: { size?: number }) => (
  <svg {...ICON_PROPS} width={size} height={size}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
    <path d="M14 2v6h6" />
  </svg>
);

const MissingFileIcon = () => (
  <svg {...ICON_PROPS} width={24} height={24}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
    <path d="M14 2v6h6" />
    <path d="m9.5 12.5 5 5" />
    <path d="m14.5 12.5-5 5" />
  </svg>
);

const formatSize = (bytes: number | null): string | null => {
  if (bytes === null || bytes < 0) return null;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const UnavailableMedia = ({ filename }: { filename: string }) => (
  <div className="flex max-w-full flex-col gap-0.5 rounded-xl border border-dashed border-stone-300 bg-stone-50 px-3 py-2.5 text-stone-600">
    <span className="flex items-center gap-2 text-sm font-medium">
      <MissingFileIcon />
      Archivo no disponible
    </span>
    {filename && <span className="truncate font-geist-mono text-xs">{filename}</span>}
    <span className="text-xs">Meta conserva los archivos 7 días</span>
  </div>
);

type Phase = 'idle' | 'loading' | 'error' | 'expired';

/** Downloads the file of a message when asked; tracks the phase and the error to show. */
const useMediaFile = (conversationId: number, messageId: number) => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [errorText, setErrorText] = useState<string | null>(null);
  const busyRef = useRef(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const load = useCallback(async (): Promise<Blob | null> => {
    if (busyRef.current) return null;
    busyRef.current = true;
    setPhase('loading');
    setErrorText(null);
    try {
      const blob = await fetchMedia(conversationId, messageId);
      if (aliveRef.current) setPhase('idle');
      return aliveRef.current ? blob : null;
    } catch (err) {
      const failure = mediaFailure(err);
      if (aliveRef.current) {
        if (failure.kind === 'expired') setPhase('expired');
        else {
          setPhase('error');
          setErrorText(failure.text);
        }
      }
      return null;
    } finally {
      busyRef.current = false;
    }
  }, [conversationId, messageId]);

  return { phase, errorText, load };
};

const ErrorLine = ({ text }: { text: string | null }) =>
  text ? (
    <span role="alert" className="text-xs font-medium text-red-700">
      {text}
    </span>
  ) : null;

const tileClass =
  'flex h-40 w-60 max-w-full flex-col items-center justify-center gap-2 rounded-xl border-0 bg-stone-100 px-3 text-stone-600 hover:bg-stone-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 disabled:cursor-wait';

const Viewer = ({
  kind,
  url,
  filename,
  onClose,
}: {
  kind: 'image' | 'video';
  url: string;
  filename: string;
  onClose: () => void;
}) => {
  const ids = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  return (
    <Modal
      role="dialog"
      titleId={`${ids}-t`}
      descriptionId={`${ids}-d`}
      maxWidth="max-w-[min(92vw,920px)]"
      topPadding="pt-[min(48px,6vh)]"
      locked={false}
      onClose={onClose}
      initialFocus={closeRef}
    >
      <h2 id={`${ids}-t`} className="truncate font-geist-mono text-sm font-medium text-stone-800">
        {filename}
      </h2>
      <p id={`${ids}-d`} className="sr-only">
        Vista del archivo adjunto
      </p>
      <div className="flex items-center justify-center">
        {kind === 'image' ? (
          <img src={url} alt={filename} className="max-h-[70vh] w-auto max-w-full rounded-xl object-contain" />
        ) : (
          <video src={url} controls autoPlay className="max-h-[70vh] w-full rounded-xl bg-stone-900" />
        )}
      </div>
      <div className="flex justify-end pt-1.5">
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          className="inline-flex h-11 items-center justify-center rounded-full border border-stone-200 bg-white px-6 text-sm font-semibold text-stone-800 hover:bg-stone-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
        >
          Cerrar
        </button>
      </div>
    </Modal>
  );
};

/** Image or video: a tile that opens the file in a viewer. */
const VisualAttachment = ({
  kind,
  conversationId,
  message,
}: {
  kind: 'image' | 'video';
  conversationId: number;
  message: ThreadMessage;
}) => {
  const { phase, errorText, load } = useMediaFile(conversationId, message.id);
  const [url, setUrl] = useState<string | null>(null);
  const tileRef = useRef<HTMLButtonElement>(null);
  const filename = message.media?.filename ?? '';

  // The object URL lives only while the viewer is open.
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );

  // Back from the viewer, the keyboard lands on the tile that opened it.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (url === null && wasOpen.current) tileRef.current?.focus();
    wasOpen.current = url !== null;
  }, [url]);

  if (phase === 'expired') return <UnavailableMedia filename={filename} />;

  const open = async () => {
    const blob = await load();
    if (blob) setUrl(URL.createObjectURL(blob));
  };
  const label = kind === 'image' ? 'Abrir imagen' : 'Abrir video';

  return (
    <div className="flex max-w-full flex-col items-start gap-1.5">
      <button
        ref={tileRef}
        type="button"
        disabled={phase === 'loading'}
        aria-busy={phase === 'loading'}
        aria-label={filename ? `${label} ${filename}` : label}
        onClick={() => void open()}
        className={tileClass}
      >
        {kind === 'image' ? <ImageIcon /> : <VideoIcon />}
        <span className="text-xs font-medium">{phase === 'loading' ? 'Abriendo…' : label}</span>
        {filename && <span className="max-w-full truncate font-geist-mono text-xs">{filename}</span>}
      </button>
      <ErrorLine text={errorText} />
      {url && <Viewer kind={kind} url={url} filename={filename || label} onClose={() => setUrl(null)} />}
    </div>
  );
};

/** Voice note: the play button fetches the file and swaps itself for the audio player. */
const AudioAttachment = ({ conversationId, message }: { conversationId: number; message: ThreadMessage }) => {
  const { phase, errorText, load } = useMediaFile(conversationId, message.id);
  const [url, setUrl] = useState<string | null>(null);

  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );

  if (phase === 'expired') return <UnavailableMedia filename={message.media?.filename ?? ''} />;

  const play = async () => {
    const blob = await load();
    if (blob) setUrl(URL.createObjectURL(blob));
  };

  return (
    <div className="flex max-w-full flex-col items-start gap-1.5">
      <div className="flex items-center gap-2.5 rounded-xl border border-stone-200 bg-white px-3 py-2 text-stone-800">
        {url ? (
          <audio src={url} controls autoPlay className="h-10 max-w-full" aria-label="Nota de voz" />
        ) : (
          <>
            <button
              type="button"
              disabled={phase === 'loading'}
              aria-busy={phase === 'loading'}
              aria-label="Reproducir nota de voz"
              onClick={() => void play()}
              className="flex h-9 w-9 flex-none items-center justify-center rounded-full border-0 bg-blue-600 text-white hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 disabled:cursor-wait disabled:opacity-70"
            >
              <PlayIcon />
            </button>
            <span className="text-sm font-medium">{phase === 'loading' ? 'Cargando…' : 'Nota de voz'}</span>
          </>
        )}
      </div>
      <ErrorLine text={errorText} />
    </div>
  );
};

/** Document: the card downloads the file with its own name. */
const DocumentAttachment = ({ conversationId, message }: { conversationId: number; message: ThreadMessage }) => {
  const { phase, errorText, load } = useMediaFile(conversationId, message.id);
  const media: MediaInfo | null = message.media;
  const size = formatSize(media?.size_bytes ?? null);

  if (phase === 'expired') return <UnavailableMedia filename={media?.filename ?? ''} />;

  const save = async () => {
    const blob = await load();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = downloadName(media?.filename, message.id);
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="flex max-w-full flex-col items-start gap-1.5">
      <button
        type="button"
        disabled={phase === 'loading'}
        aria-busy={phase === 'loading'}
        aria-label={`Descargar ${media?.filename || 'documento'}`}
        onClick={() => void save()}
        className="flex max-w-full items-center gap-3 rounded-xl border border-stone-200 bg-white px-3 py-2.5 text-left text-stone-900 hover:bg-stone-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600 disabled:cursor-wait"
      >
        <span className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-red-100 text-red-700">
          <FileIcon size={20} />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold">{media?.filename || 'Documento'}</span>
          <span className="font-geist-mono text-xs text-stone-600">
            {phase === 'loading' ? 'Descargando…' : (size ?? '')}
          </span>
        </span>
      </button>
      <ErrorLine text={errorText} />
    </div>
  );
};

/** The body of a message that carries a file: image, video, voice note or document. */
export const AttachmentContent = ({
  conversationId,
  message,
}: {
  conversationId: number;
  message: ThreadMessage;
}): ReactNode => {
  const kind: AttachmentKind | null = attachmentKind(message.type);
  if (kind === null) return null;
  // The server already knows the file is gone (more than 7 days).
  if (message.media && !message.media.available) return <UnavailableMedia filename={message.media.filename} />;
  switch (kind) {
    case 'image':
    case 'video':
      return <VisualAttachment kind={kind} conversationId={conversationId} message={message} />;
    case 'audio':
      return <AudioAttachment conversationId={conversationId} message={message} />;
    case 'document':
      return <DocumentAttachment conversationId={conversationId} message={message} />;
  }
};
